/* global REACT_APP_PUBLIC_ENABLE_TRACKING */
/* global REACT_APP_PUBLIC_ENABLE_POSTHOG */
/* global REACT_APP_PUBLIC_CLARITY_PROJECT_ID */
/* global REACT_APP_PUBLIC_GA4_MEASUREMENT_ID */
/* global REACT_APP_PUBLIC_POSTHOG_KEY */
/* global REACT_APP_PUBLIC_POSTHOG_HOST */
/* global REACT_APP_PUBLIC_SENTRY_DNS */
/* global REACT_APP_PUBLIC_SENTRY_ENVIRONMENT */

import { ChatwootProvider } from '@context/ChatwootContext';
import { AppErrorFallback } from '@components/ErrorBoundary/AppErrorBoundary';
import * as Sentry from '@sentry/react';
import { PostHogProvider } from 'posthog-js/react';
import { ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import App from './App';
import PartnerApp from './partner/PartnerApp';
import { AuthComponent } from './context/AuthContext';
import { appStore } from './store';
import { initTelemetry } from '@utils/analytics/telemetry';

import { registerSW } from 'virtual:pwa-register';
import './index.css';

// Register service worker
registerSW({
  onNeedRefresh() {
    // eslint-disable-next-line no-console
    console.log('[PWA] New content available, please refresh.');
  },
  onOfflineReady() {
    // eslint-disable-next-line no-console
    console.log('[PWA] App ready to work offline');
  },
  onRegistered(registration: ServiceWorkerRegistration | undefined) {
    // eslint-disable-next-line no-console
    console.log('[PWA] Service Worker registered:', registration);
  },
  onRegisterError(error: Error) {
    // eslint-disable-next-line no-console
    console.error('[PWA] Service Worker registration error:', error);
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        feature: 'pwa',
        action: 'sw_registration_error',
      },
      extra: {
        errorMessage: error.message,
        errorStack: error.stack,
      },
    });
  },
});

const rootElement = document.getElementById('app');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(rootElement!);

const isDev = REACT_APP_PUBLIC_SENTRY_ENVIRONMENT !== 'production';
const isProd = REACT_APP_PUBLIC_SENTRY_ENVIRONMENT === 'production';
// prod: 10% — bajado de 0.4 para reducir el volumen de spans (~6M→~1.5M/mes).
// 0.1 mantiene muestra estadística suficiente para un panel con este tráfico.
const tracesSampleRate = isDev ? 1.0 : 0.1;
if (isProd) {
  Sentry.init({
    dsn: REACT_APP_PUBLIC_SENTRY_DNS,
    debug: false,
    environment: REACT_APP_PUBLIC_SENTRY_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracePropagationTargets: ['127.0.0.1', 'localhost', /^https:\/\/amazonaws\.com\//],
    // Performance Monitoring
    tracesSampleRate: tracesSampleRate,
    // Session Replay — solo grabamos sesiones que tuvieron un error (antes 0.1 grababa el 10% de TODAS)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Ruido conocido de navegador/terceros que no es accionable.
    // Patrones acotados a propósito: NO usar /NotAllowedError/ ni /AbortError: Registration failed/
    // genéricos — taparían errores reales de clipboard/notificaciones y el captureException de
    // onRegisterError (registro del Service Worker). El ruido de push ya lo cubre /push service not available/.
    ignoreErrors: [
      /play\(\) failed/,
      /play\(\) request was interrupted/,
      /play method is not allowed/,
      /messaging\/unsupported-browser/,
      /push service not available/,
    ],
    // Red de seguridad: nunca enviar telemetría informativa al pipeline de errores
    beforeSend(event) {
      if (event.level === 'info' || event.level === 'log') {
        return null;
      }
      return event;
    },
  });
}

// Telemetría no-PostHog: Clarity + GA4 (gtag) + consent default.
// Misma condición de carga que PostHog (REACT_APP_PUBLIC_ENABLE_TRACKING).
if (REACT_APP_PUBLIC_ENABLE_TRACKING) {
  initTelemetry({
    clarityProjectId: REACT_APP_PUBLIC_CLARITY_PROJECT_ID,
    ga4MeasurementId: REACT_APP_PUBLIC_GA4_MEASUREMENT_ID,
  });
}

// PostHog se monta solo si el tracking global está activo Y el flag dedicado de PostHog
// está encendido. Apagar PostHog (flag en false) NO afecta a Clarity/GA4 (initTelemetry,
// gateado por ENABLE_TRACKING) ni a Sentry (independiente).
const enablePostHog = Boolean(REACT_APP_PUBLIC_ENABLE_TRACKING) && REACT_APP_PUBLIC_ENABLE_POSTHOG;

const PostHogHOC = ({ children }: { children: ReactNode }) => {
  return !enablePostHog ? (
    children
  ) : (
    <PostHogProvider
      apiKey={REACT_APP_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: REACT_APP_PUBLIC_POSTHOG_HOST,
        opt_in_site_apps: true,
      }}
    >
      {children}
    </PostHogProvider>
  );
};

// PRD-037: el portal del partner y el claim son una APP SEPARADA (router/auth propios). Se montan
// aqui, en el entry, sin los providers del panel (AuthContext/Redux/Chatwoot) para evitar
// contaminacion/conflictos. Solo comparten el ErrorBoundary y la telemetria gateada por env.
const partnerPath = window.location.pathname;
const isPartnerApp = partnerPath.startsWith('/partner') || partnerPath.startsWith('/pre-registro');

if (isPartnerApp) {
  root.render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
        <PartnerApp />
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
} else {
  root.render(
    <StrictMode>
      <Provider store={appStore}>
        <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
          <AuthComponent>
            <PostHogHOC>
              <ChatwootProvider>
                <App />
              </ChatwootProvider>
            </PostHogHOC>
          </AuthComponent>
        </Sentry.ErrorBoundary>
      </Provider>
    </StrictMode>,
  );
}
